"use client";

/**
 * Wake watchdog (sleep/wake freshness gate, proposal 23).
 *
 * When the OS suspends, JS timers and the event loop freeze while the TCP socket
 * is left dangling. On resume the browser drains a burst of buffered pre-sleep
 * frames into the quote store — and because the socket still looks "connected",
 * SignalR's automatic reconnect never fires. This watchdog detects the resume
 * three ways and, on any of them, marks the feed stale, hard-recycles the suspect
 * socket, and runs the snapshot-resync:
 *
 *   1. a monotonic Date.now() gap larger than a threshold (the timer that should
 *      have fired every ~1s fired much later — the loop was frozen),
 *   2. document `visibilitychange` back to visible after being hidden > N s,
 *   3. window `online`, and (desktop) Electron powerMonitor `resume` if exposed.
 *
 * The recovery flips freshness `stale → resyncing → live` as the snapshot lands.
 */

import { useEffect, useRef } from "react";
import type { VybeTraderClient } from "../client/VybeTraderClient";
import { useConnectionFreshnessStore } from "../stores/useConnectionFreshnessStore";

/** How often the monotonic heartbeat ticks. */
const HEARTBEAT_MS = 1000;
/**
 * A gap larger than this between heartbeats means the event loop was frozen
 * (machine slept / heavy suspend) rather than a normal 1s tick. Deliberately
 * well above the interval + jitter so a brief tab-switch or GC pause won't trip
 * it and recycle a healthy connection.
 */
const WAKE_GAP_MS = 10000;
/**
 * Only treat a return-to-visible as a wake if the tab was hidden at least this long. Kept short:
 * iOS/Android suspend the WebView and kill the socket within a few seconds of backgrounding, so the
 * old 30s gate let short suspends fall through to the 40s server-timeout path — the "takes a while
 * to connect from suspend" the SR Global client reported (2026-09-08). Recovery is debounced anyway.
 * Raised 2s → 5s on 2026-09-09: at 2s an ordinary app-switch tore down and rebuilt a healthy socket,
 * and the reconnect work landed in front of the user's own trade commands. A real mobile suspend is
 * comfortably longer than 5s, and the monotonic-gap and `online` triggers still catch the rest.
 */
const HIDDEN_WAKE_MS = 5000;
/** Debounce so overlapping signals (gap + visibility + online) recover only once. */
const RECOVER_DEBOUNCE_MS = 5000;
/**
 * Upper bound on one recycle. SignalR puts no timeout on the WebSocket open/handshake, and on iOS a
 * socket opened while the app was being suspended can sit pending indefinitely — which used to hold
 * `recoveringRef` and silently drop every later wake signal.
 */
const RECYCLE_TIMEOUT_MS = 30000;

export function useWakeWatchdog(client: VybeTraderClient): void {
  const lastBeatRef = useRef<number>(Date.now());
  const hiddenSinceRef = useRef<number | null>(null);
  const recoveringRef = useRef<boolean>(false);
  const lastRecoverAtRef = useRef<number>(0);

  useEffect(() => {
    // The recovery: mark stale, recycle the suspect socket, snapshot-resync,
    // then return to live. Debounced + guarded so concurrent wake signals fold
    // into a single recovery.
    async function recover(reason: string): Promise<void> {
      const now = Date.now();
      if (recoveringRef.current) return;
      if (now - lastRecoverAtRef.current < RECOVER_DEBOUNCE_MS) return;
      // Recover even when the socket has ALREADY dropped (status "disconnected" after onclose):
      // that is exactly the post-suspend state, and nothing else re-dials — useAccountConnection
      // only fires on an account change. recycle() re-connects from the last credentials and is a
      // no-op when there never was a session, so there is nothing to guard here.

      recoveringRef.current = true;
      lastRecoverAtRef.current = now;
      const fresh = useConnectionFreshnessStore.getState();

      try {
        // 1) Freeze last-good and show STALE immediately.
        fresh.markStale();

        // 2) Hard-recycle the suspect socket (force-stop → fresh connect()).
        //    connect() with hasConnectedBefore already triggers the built-in
        //    resync; we also drive freshness state around it and call the
        //    shared snapshot-resync explicitly for the wake path.
        fresh.markResyncing();
        await Promise.race([
          client.recycle(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("recycle timed out")),
              RECYCLE_TIMEOUT_MS
            )
          ),
        ]);

        // 3) Belt-and-braces snapshot-resync (idempotent server-side).
        client.resyncMarketData();

        // 4) Back to live once the snapshot is in flight / applied.
        fresh.markLive();
      } catch {
        // Recycle failed (still offline) — leave the banner STALE; the next
        // wake signal or SignalR reconnect will retry.
        fresh.markStale();
      } finally {
        recoveringRef.current = false;
      }

      // recycle() can only re-dial a session it still holds credentials for. After a 45s connect
      // deadline, an exhausted auto-reconnect, or a page-unload disconnect the client has none —
      // and until now that made every wake a silent no-op, with nothing else ever re-dialling the
      // trading gateway. Hand the job back to useAccountConnection via the accounts store instead.
      if (client.getConnectionStatus() !== "connected") {
        void import("@/lib/accounts/store")
          .then(({ useAccountsStore }) => {
            const { activeAccountId, requestReconnect } = useAccountsStore.getState();
            if (activeAccountId) requestReconnect();
          })
          .catch(() => {
            /* accounts store unavailable — nothing to redial */
          });
      }
    }

    // (1) Monotonic heartbeat — a large gap means the loop was frozen.
    lastBeatRef.current = Date.now();
    const heartbeat = setInterval(() => {
      const now = Date.now();
      const gap = now - lastBeatRef.current;
      lastBeatRef.current = now;
      if (gap > WAKE_GAP_MS) {
        void recover(`monotonic gap ${Math.round(gap / 1000)}s`);
      }
    }, HEARTBEAT_MS);

    // (2) Visibility — returning to visible after a long hidden stretch.
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now();
      } else {
        const since = hiddenSinceRef.current;
        hiddenSinceRef.current = null;
        // Reset the heartbeat baseline so a long-hidden background tab that kept
        // firing throttled beats doesn't also trip the gap check.
        lastBeatRef.current = Date.now();
        if (since !== null && Date.now() - since > HIDDEN_WAKE_MS) {
          void recover("returned to visible after long hidden");
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // (3) Network came back.
    const onOnline = () => {
      lastBeatRef.current = Date.now();
      void recover("network online");
    };
    window.addEventListener("online", onOnline);

    // (desktop) Electron power resume, if the shell exposes it on window.
    const powerResume = () => void recover("powerMonitor resume");
    const electronApi = (
      window as unknown as {
        electron?: { onPowerResume?: (cb: () => void) => (() => void) | void };
      }
    ).electron;
    const removePowerListener = electronApi?.onPowerResume?.(powerResume);

    return () => {
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      if (typeof removePowerListener === "function") removePowerListener();
    };
  }, [client]);
}
